// src/App.tsx
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./store/auth";
import RequireAuth from "./components/RequireAuth";
import { ToastProvider } from "./components/ui/ToastProvider";
import PageSkeleton from "./components/common/PageSkeleton";

/** 페이지 이동 시 sticky :hover / focus 잔여로 첫 탭이 씹히는 현상 완화 */
function ClearTouchHoverOnNavigate() {
  const { pathname } = useLocation();
  useEffect(() => {
    // pathname만 본다. search 변경 시 blur하면 검색창 포커스가 풀리고
    // Backspace가 브라우저 뒤로가기로 동작한다.
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
  }, [pathname]);
  return null;
}

const ReportPdfPage = lazy(() => import("./pages/Report/ReportPdfPage"));
const SettingsPage = lazy(() => import("./pages/Settings"));

const LoginPage = lazy(() => import("./pages/Login/LoginPage"));
const DashboardPage = lazy(() => import("./pages/Dashboard/DashboardPage"));

const WorkloadPage = lazy(() => import("./pages/Workload/WorkloadPage"));
const WorkloadDetailPage = lazy(() => import("./pages/Workload/WorkloadDetailPage"));

const CreationPage = lazy(() => import("./pages/Report/ReportCreatePage"));
const ReportListPage = lazy(() => import("./pages/Report/ReportListPage"));
const ReportEditPage = lazy(() => import("./pages/Report/ReportEditPage"));
const ReportViewPage = lazy(() => import("./pages/Report/ReportViewPage"));
const TbmListPage = lazy(() => import("./pages/TBM/TbmListPage"));
const TbmCreatePage = lazy(() => import("./pages/TBM/TbmCreatePage"));
const TbmDetailPage = lazy(() => import("./pages/TBM/TbmDetailPage"));
const TbmPdfPage = lazy(() => import("./pages/TBM/TbmPdfPage"));

const VacationPage = lazy(() => import("./pages/Vacation/VacationPage"));
const AdminVacationPage = lazy(() => import("./pages/Vacation/AdminVacationPage"));

const PersonalExpensePage = lazy(() => import("./pages/Expense/PersonalExpensePage"));
const MemberExpensePage = lazy(() => import("./pages/Expense/MemberExpensePage"));
const MaterialsPage = lazy(() => import("./pages/Materials/MaterialsPage"));
const MaterialPurchasePage = lazy(() => import("./pages/Materials/MaterialPurchasePage"));

const MembersPage = lazy(() => import("./pages/Members/MembersPage"));
const VehiclesPage = lazy(() => import("./pages/Vehicles/VehiclesPage"));
const EmailSettingsPage = lazy(() => import("./pages/Settings/Email"));
const SafePhrasePage = lazy(() => import("./pages/Settings/SafePhrase"));
const WebNotificationSettingsPage = lazy(() => import("./pages/Settings/WebNotificationSettingsPage"));
const BoardListPage = lazy(() => import("./pages/Board/BoardListPage"));
const BoardCreatePage = lazy(() => import("./pages/Board/BoardCreatePage"));
const BoardEditPage = lazy(() => import("./pages/Board/BoardEditPage"));
const InvoicePage = lazy(() => import("./pages/Invoice/InvoicePage"));
const InvoiceDraftListPage = lazy(() => import("./pages/Invoice/InvoiceDraftListPage"));
const InvoiceCreatePage = lazy(() => import("./pages/Invoice/InvoiceCreatePage"));
const ScheduleListPage = lazy(() => import("./pages/Schedule/ScheduleListPage"));
const ScheduleCreatePage = lazy(() => import("./pages/Schedule/ScheduleCreatePage"));


function RoleLanding() {
  const { loading, loadingProfile, user, profile } = useAuth();

  if (loading || loadingProfile) return <PageSkeleton />;
  if (!user) return <Navigate to="/login" replace />;

  if (profile?.role === "admin") return <Navigate to="/dashboard" replace />;
  return <Navigate to="/report" replace />;
}

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <BrowserRouter>
          <ClearTouchHoverOnNavigate />
          <Suspense fallback={<PageSkeleton />}>
            <Routes>
              {/* public */}
              <Route path="/login" element={<LoginPage />} />

              {/* protected */}
              <Route element={<RequireAuth />}>
                <Route path="/dashboard" element={<DashboardPage />} />

                <Route path="/workload" element={<WorkloadPage />} />
                <Route path="/workload/detail/:id" element={<WorkloadDetailPage />} />

                <Route path="/reportcreate" element={<CreationPage />} />
                <Route path="/report" element={<ReportListPage />} />
                <Route path="/report/:id" element={<ReportViewPage />} />
                <Route path="/report/:id/edit" element={<ReportEditPage />} />
                <Route path="/report/pdf" element={<ReportPdfPage />} />

                <Route path="/tbm" element={<TbmListPage />} />
                <Route path="/tbm/create" element={<TbmCreatePage />} />
                <Route path="/tbm/:id/pdf" element={<TbmPdfPage />} />
                <Route path="/tbm/:id" element={<TbmDetailPage />} />

                <Route path="/vacation" element={<VacationPage />} />
                <Route path="/vacation/admin" element={<AdminVacationPage />} />

                <Route path="/expense" element={<PersonalExpensePage />} />
                <Route path="/expense/member" element={<MemberExpensePage />} />
                <Route path="/materials/create" element={<MaterialPurchasePage />} />
                <Route path="/materials" element={<MaterialsPage title="목록" />} />

                <Route path="/board" element={<BoardListPage />} />
                <Route path="/board/create" element={<BoardCreatePage />} />
                <Route path="/board/:id/edit" element={<BoardEditPage />} />
                <Route path="/board/:id" element={<Navigate to="/board" replace />} />

                <Route path="/schedule" element={<ScheduleListPage />} />
                <Route path="/schedule/create" element={<ScheduleCreatePage />} />

                <Route path="/members" element={<MembersPage />} />
                <Route path="/vehicles" element={<VehiclesPage />} />
                <Route path="/invoice" element={<InvoicePage />} />
                <Route path="/invoice/drafts" element={<InvoiceDraftListPage />} />
                <Route path="/invoice/create" element={<InvoiceCreatePage />} />

                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/email" element={<EmailSettingsPage />} />
                <Route path="/settings/web-notification" element={<WebNotificationSettingsPage />} />
                <Route path="/settings/safe-phrase" element={<SafePhrasePage />} />


                {/* backward compatible */}
                <Route
                  path="/settings/email-notifications"
                  element={<Navigate to="/settings/email" replace />}
                />
              </Route>

              {/* default */}
              <Route path="/" element={<RoleLanding />} />
              <Route path="*" element={<RoleLanding />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
